<template>
	<div class="container-fluid p-4">
		<!-- The way back: this page is reached from the instance table on
		     /_/traps and is the end of that line, so the list it came from is
		     the only navigation it owes. -->
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Trap Instance{{ idLabel }}</h1>
				<p class="text-muted mb-0">
					One trap put out at one place and time (§24), the catch it made, and
					the sample it produced.
				</p>
			</div>
			<RouterLink to="/_/traps" class="btn btn-outline-secondary btn-sm">
				<i class="bi bi-arrow-left"></i> All traps
			</RouterLink>
		</div>

		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
			<p class="mt-2 text-muted">Loading trap instance…</p>
		</div>

		<div v-else-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<!-- A miss is an empty answer rather than a failure: the read is narrowed
		     to the caller's own district, so another district's instance, an id
		     that does not exist and an id that is not a number all read the same
		     way. The page states the id it looked for and points back at the
		     list it came from; a segment that states no id at all says that
		     instead of spelling one. -->
		<div v-else-if="!row" class="card">
			<div class="card-body text-center py-4 text-muted">
				<p class="mb-3">{{ notFoundText }}</p>
				<RouterLink to="/_/traps" class="btn btn-outline-secondary btn-sm">
					Back to traps
				</RouterLink>
			</div>
		</div>

		<div v-else class="row g-4">
			<div class="col-12 col-lg-8">
				<!-- §24's instance: what the operator stated when the trap went
				     out, and what the trap itself did. Location and Interval read
				     `—` for an instance whose set form stated no coordinates or
				     which is still out; Station reads `—` for an off-station
				     instance, which is normal rather than a gap. -->
				<div class="card">
					<div class="card-header">Instance</div>
					<div class="card-body">
						<dl class="row mb-0">
							<dt class="col-sm-4 text-muted">State</dt>
							<dd class="col-sm-8">
								<span
									class="badge rounded-pill"
									:class="
										row.state === 'collected' ? 'bg-success' : 'bg-secondary'
									"
								>
									{{ INSTANCE_STATE_LABELS[row.state] ?? row.state }}
								</span>
							</dd>

							<dt class="col-sm-4 text-muted">Set</dt>
							<dd class="col-sm-8">{{ formatInstant(row.date) }}</dd>

							<dt class="col-sm-4 text-muted">Collected</dt>
							<dd class="col-sm-8">{{ collectedText(row) }}</dd>

							<dt class="col-sm-4 text-muted">Interval</dt>
							<dd class="col-sm-8">{{ intervalText(row) }}</dd>

							<dt class="col-sm-4 text-muted">Location</dt>
							<dd class="col-sm-8">{{ formatLocation(row) }}</dd>

							<dt class="col-sm-4 text-muted">Site</dt>
							<dd class="col-sm-8">{{ row.site || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Station</dt>
							<dd class="col-sm-8">{{ row.station || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Trap type</dt>
							<dd class="col-sm-8">{{ row.trap_type || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Lure</dt>
							<dd class="col-sm-8">{{ row.lure || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Positivity</dt>
							<dd class="col-sm-8">
								{{ POSITIVITY_LABELS[row.positivity] ?? row.positivity }}
							</dd>
						</dl>
					</div>
				</div>

				<!-- The catch: the counts cell's three-valued reading, then the
				     whole species × sex composition and the pools drawn from it.
				     A Set instance has no sample to have counted and a collected
				     one whose composition is not yet known says which silence it
				     is, so neither is ever drawn as a zero catch. -->
				<div class="card mt-4">
					<div class="card-header">Catch</div>
					<div class="card-body">
						<p
							v-if="row.counts.state === 'not_collected'"
							class="text-muted mb-0"
						>
							Not collected: the instance is Set, so no sample exists yet.
						</p>
						<p
							v-else-if="row.counts.state === 'not_analyzed'"
							class="text-muted mb-0"
						>
							Not analyzed: the sample's composition is not yet known.
						</p>
						<template v-else>
							<dl class="row mb-0">
								<dt class="col-sm-4 text-muted">Total</dt>
								<dd class="col-sm-8">
									{{ row.counts.total.toLocaleString() }}
								</dd>

								<dt class="col-sm-4 text-muted">Female</dt>
								<dd class="col-sm-8">
									{{ row.counts.female.toLocaleString() }}
								</dd>

								<dt class="col-sm-4 text-muted">Pools</dt>
								<dd class="col-sm-8">{{ row.pools.toLocaleString() }}</dd>
							</dl>

							<div
								v-if="catchSpecies.rows.length === 0"
								class="text-muted mt-3"
							>
								Analyzed: no species were caught.
							</div>
							<div v-else class="table-responsive mt-3">
								<!-- One row per species and one column per sex the
								     composition states, in the matrix's own order:
								     the composition decides which sexes exist, so
								     the table is built from it rather than from a
								     fixed pair. -->
								<table class="table table-sm table-striped align-middle mb-0">
									<thead class="table-light">
										<tr>
											<th scope="col">Species</th>
											<th
												v-for="sex in catchSpecies.sexes"
												:key="sex"
												scope="col"
												class="text-end"
											>
												{{ sex }}
											</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="line in catchSpecies.rows" :key="line.id">
											<td>{{ line.species }}</td>
											<td
												v-for="sex in catchSpecies.sexes"
												:key="sex"
												class="text-end"
											>
												{{ matrixCell(line, sex) }}
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</template>
					</div>
				</div>
			</div>

			<div class="col-12 col-lg-4">
				<!-- §25's sample: the specimen the catch came from. Its label and
				     its analysis state are the sample's own facts, which the
				     instance row does not carry — the row names the sample and
				     this card reads it. A null label is ordinary here rather than
				     a failed read, so it renders `—`. -->
				<div class="card">
					<div class="card-header">Sample</div>
					<div class="card-body">
						<p v-if="row.sample_id == null" class="text-muted mb-0">
							No sample collected yet.
						</p>
						<dl v-else class="row mb-0">
							<dt class="col-sm-5 text-muted">Sample</dt>
							<dd class="col-sm-7">#{{ row.sample_id }}</dd>

							<dt class="col-sm-5 text-muted">Label</dt>
							<dd class="col-sm-7">{{ labelText(sample) }}</dd>

							<dt class="col-sm-5 text-muted">Analysis</dt>
							<dd class="col-sm-7">{{ analysisStateText(sample) }}</dd>
						</dl>

						<!-- The sample's read is its own request, kept off the
						     instance's failure path: the instance is the page's
						     subject and its facts are already in hand, so a sample
						     the district no longer holds is reported here rather
						     than replacing the page with an error. -->
						<p v-if="sampleError" class="text-muted small mt-2 mb-0">
							{{ sampleError }}
						</p>
					</div>
				</div>

				<!-- The code stuck to the trap's hardware: it encodes the
				     instance's public URL (§7.2, Q-T1), whose id is the id in
				     this path. The office's mint is a session-authenticated route
				     on this same origin, so a plain `<img>` carries the session
				     cookie — the same way the virtual-inspection photos do
				     (CardVirtualInspection.vue's `/api/image/{uuid}/content`). -->
				<div class="card mt-4">
					<div class="card-header">QR Code</div>
					<div class="card-body text-center">
						<img
							:src="`/api/qr-code/trap-instance/${id}`"
							:alt="`Trap instance ${id} QR code`"
							class="img-fluid"
							width="320"
							height="320"
						/>
						<p class="text-muted small mb-0 mt-2">
							The code stuck to this instance's trap hardware.
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink, useRoute } from "vue-router";

import { apiClient } from "@/client";
import {
	INSTANCE_STATE_LABELS,
	POSITIVITY_LABELS,
} from "@/components/map-review/columns";
import { formatDate } from "@/format";
import type {
	LabSampleSubject,
	LabTrapSampleDetail,
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
	MapReviewTrapInstanceRow,
} from "@/type/api";

const route = useRoute();
// The instance is addressed by the id in the path — the same id the printed QR
// carries (§7.2, Q-T1), so one number names the instance on the printed code,
// in the office's mint and on this page.
const id = Number(route.params.id);
// The id as the page states it. A path segment that is not a number names no
// instance and has no id to print, so the heading and the not-found line say
// nothing about it rather than spelling NaN.
const idLabel = computed(() => (Number.isFinite(id) ? `#${id}` : ""));
const notFoundText = computed(() =>
	Number.isFinite(id)
		? `No trap instance ${id} in your district.`
		: "That is not a trap instance address.",
);

// The row is read through the resource's own QUERY route rather than a GET by
// id, because a Trap Instance has no by-id read: `id equal` is the narrowing
// the ledger's own grid uses on this property, and `limit: 1` keeps it one row.
const row = ref<MapReviewTrapInstanceRow | null>(null);
// §25's own facts, read from the sample's endpoint once the row names a sample:
// the instance row carries what the sample produced (counts, pools, positivity)
// and never the specimen's label or its analysis state.
const sample = ref<LabSampleSubject | null>(null);
const sampleError = ref("");

const loading = ref(false);
const error = ref("");

/** The sample's analysis state, spelled as §25's own two states. */
const ANALYSIS_STATE_LABELS: Record<string, string> = {
	not_analyzed: "Not analyzed",
	analyzed: "Analyzed",
};

function formatInstant(value: string): string {
	return formatDate(new Date(value));
}

function formatLocation(instance: MapReviewTrapInstanceRow): string {
	if (instance.latitude === null || instance.longitude === null) return "—";
	return `${instance.latitude}, ${instance.longitude}`;
}

// An instance still out has no collect datetime and no interval: both derive
// from the collect (§24), so they read `—` rather than a duration measured from
// a moment the instance never reached.
function collectedText(instance: MapReviewTrapInstanceRow): string {
	return instance.collected_at ? formatInstant(instance.collected_at) : "—";
}

function intervalText(instance: MapReviewTrapInstanceRow): string {
	return instance.interval === null ? "—" : `${instance.interval} day(s)`;
}

// A pending sample read and a district's samples that genuinely carry no label
// would otherwise look the same, so an absent label reads `—` either way — the
// label is human-facing and its absence is ordinary, not a failed read.
function labelText(subject: LabSampleSubject | null): string {
	return subject?.label ?? "—";
}

function analysisStateText(subject: LabSampleSubject | null): string {
	if (!subject) return "—";
	return ANALYSIS_STATE_LABELS[subject.analysis_state];
}

/** One species' line of the counts matrix: the species, and its count by each
 * sex the composition named it under. */
interface SpeciesLine {
	id: number;
	species: string;
	counts: Map<string, number>;
}

/** The counts matrix as a species × sex table: one row per species and one
 * column per sex the composition states, in the matrix's own order. A pair the
 * composition does not carry is a blank cell, never a zero — a species with no
 * females recorded is not a species somebody counted and found none. */
const catchSpecies = computed<{
	sexes: string[];
	rows: SpeciesLine[];
}>(() => {
	const sexes: string[] = [];
	const rows = new Map<number, SpeciesLine>();
	for (const entry of row.value?.counts.matrix ?? []) {
		if (!sexes.includes(entry.sex)) sexes.push(entry.sex);
		let line = rows.get(entry.species_id);
		if (!line) {
			line = {
				id: entry.species_id,
				species: entry.species,
				counts: new Map(),
			};
			rows.set(entry.species_id, line);
		}
		line.counts.set(entry.sex, entry.count);
	}
	return { sexes, rows: [...rows.values()] };
});

/** One cell of that table: the count, or `—` for a pair the composition does
 * not carry. */
function matrixCell(line: SpeciesLine, sex: string): string {
	const count = line.counts.get(sex);
	return count === undefined ? "—" : count.toLocaleString();
}

async function loadSample(sampleID: number) {
	try {
		const detail = await apiClient.JSONGet<LabTrapSampleDetail>(
			`/api/trap-sample/${sampleID}`,
		);
		sample.value = detail.sample;
	} catch (e: any) {
		sampleError.value =
			e?.response?.data?.message || e?.message || "Failed to load the sample.";
	}
}

// One row, narrowed to the caller's district: an instance of another district
// and an id that does not exist both answer with no items, which is the empty
// answer this page renders as not-found. A path segment that is not a number
// names no instance either, and asking would send the filter a null value — the
// page answers not-found without the request.
async function load() {
	if (!Number.isFinite(id)) {
		row.value = null;
		return;
	}
	loading.value = true;
	error.value = "";
	try {
		const envelope = await apiClient.JSONQuery<
			MapReviewQueryEnvelope<MapReviewTrapInstanceRow>
		>("/api/trap-instance", {
			filter: [{ property: "id", operator: "equal", value: id }],
			limit: 1,
		} satisfies MapReviewQueryBody);
		row.value = envelope.items[0] ?? null;
		if (row.value?.sample_id != null) {
			await loadSample(row.value.sample_id);
		}
	} catch (e: any) {
		error.value =
			e?.response?.data?.message ||
			e?.message ||
			"Failed to load the trap instance.";
	} finally {
		loading.value = false;
	}
}

onMounted(load);
</script>
